import * as stylex from "@stylexjs/stylex";

export const measure = stylex.defineVars({
	spacingHorizontalMin: "2px",
	spacingHorizontalSmaller: "16px",
	spacingHorizontalDefault: "24px",
	spacingVerticalMin: "2px",
	spacingVerticalSmaller: "8px",
	spacingVerticalDefault: "12px",
	spacingVerticalLarger: "16px",
	spacingVerticalMax: "24px",

	spacingGapMin: "4px",
	spacingGapNarrow: "8px",
	spacingGapSmaller: "12px",
	spacingGapDefault: "16px",
	spacingGapLarger: "24px",
	spacingGapMax: "48px",

	sizeMin: "4px",
	sizeXs: "8px",
	sizeS: "16px",
	sizeM: "24px",
	sizeL: "32px",
	sizeXl: "40px",
	sizeXxl: "48px",
	sizeXxxl: "56px",

	sizeIconDefault: "24px",
	sizeIconXl: "48px",
	sizeControlsButton: "48px",
	sizeControlsInput: "48px",
});

export const breakpoints = stylex.defineConsts({
	sm: "@media (min-width: 640px)",
	md: "@media (min-width: 800px)",
	lg: "@media (min-width: 960px)",
	xl: "@media (min-width: 1272px)",
});

export const colors = stylex.defineVars({
	// Neutral
	neutralTxtDefault: "#001536",
	neutralTxtDefaultActive: "#000c1f",
	neutralTxtWeak: "#576680",
	neutralTxtModerate: "#576680",
	neutralTxtInverted: "#FFFFFF",
	neutralTxtInvertedActive: "#FFFFFF",

	neutralBgDefault: "#FFFFFF",
	neutralBgDefaultActive: "#f5f7fb",
	neutralBgDeep: "#f5f7fb",
	neutralBgDelicate: "#e9ecf0",
	neutralBgWeak: "#e9ecf0",
	neutralBgModerate: "#146899",
	neutralBgStrong: "#576680",
	neutralBgStrongest: "#001536",
	neutralBgElevated: "#ffffff",

	neutralBorderDefault: "rgba(48,50,51,0.12)",
	neutralBorderDefaultActive: "rgba(48,50,51,0.16)",
	neutralBorderWeak: "rgba(48,50,51,0.08)",
	neutralBorderModerate: "#146899",
	neutralBorderStrong: "rgba(0,21,54,0.8)",

	// Accent / txt
	accentTxtDefault: "#0066d4",
	accentTxtDefaultActive: "#0056b2",
	accentTxtStrong: "#02193e",
	accentTxtStrongActive: "#042e72",
	accentTxtInverted: "#ffffff",
	accentTxtInvertedActive: "#ffffff",

	// Accent / bg
	accentBgStrong: "#0071eb",
	accentBgStrongActive: "#0065d1",
	accentBgWeak: "#e6f2fe",
	accentBgWeakActive: "#c7e3ff",
	accentBgStrongest: "#001536",
	accentBgStrongestActive: "#001f52",

	// Accent / border
	accentBorderDefault: "#0071eb",
	accentBorderDefaultActive: "#0065d1",
	accentBorderStrong: "#001536",
	accentBorderStrongActive: "#001f52",

	// Disabled / txt
	disabledTxtDefault: "#b5b8bd",
	disabledTxtModerate: "#768499",

	// Disabled / bg
	disabledBgDefault: "#d7dbe0",

	// Disabled / border
	disabledBorderDefault: "rgba(48,50,51,0.04)",
	disabledBorderStrong: "#7a889e",

	// Success / txt
	successTxtDefault: "#297856",
	successTxtStrong: "#054F21",
	successTxtInverted: "#c5ffc6",

	// Success / bg
	successBgStrong: "#107046",
	successBgWeak: "#c5ffc6",

	// Success / border
	successBorderDefault: "#107046",
	successBorderStrong: "#054f21",

	// Warning / txt
	warningTxtDefault: "#b84412",
	warningTxtDefaultActive: "#8b330e",
	warningTxtInverted: "#3f2206",
	warningTxtStrong: "#423007",
	warningTxtModerate: "#7a5f50",

	// Warning / bg
	warningBgStrong: "#ff993a",
	warningBgWeak: "#ffe7d1",
	warningBgWeakActive: "#f6eada",

	// Danger / txt
	dangerTxtDefault: "#ba303b",
	dangerTxtInverted: "#ffffff",

	// Danger / bg
	dangerBgStrong: "#c11417",
	dangerBgStrongActive: "#9d1013",
	dangerBgWeak: "#ffdae0",

	spaceObPrimary: "#054752",
	spaceObSecondary: "#FF595A",
});

export const radius = stylex.defineVars({
	weak: "8px",
	default: "16px",
	moderate: "12px",
	strong: "24px",
	round: "9999px",
});

export const shadow = stylex.defineVars({
	neutralDefault: "0 1px 8px 0 rgba(0,0,0,0.16)",
	neutralDefaultActive: "0 4px 16px 0 rgba(0,0,0,0.16)",
	neutralWeak: "0 1px 2px 1px rgba(0,0,0,0.12)",
	neutralModerateMobile: "0 9px 8px -8px rgba(0,0,0,0.16)",
	neutralModerateDesktop: "0 16px 16px -16px rgba(0,0,0,0.16)",
	neutralStrongDesktop: "0 4px 14px 0 rgba(0,0,0,0.2)",
	neutralElevated: "0 36px 36px 0 rgba(0,0,0,0.3)",
	neutralStickyBottom: "0 -4px 8px 1px rgba(0,0,0,0.16)",
	neutralStickyTop: "0 -4px 8px 1px rgba(0, 0, 0, 0.16)",
});

export const typo = stylex.defineVars({
	fontFamily: "gt-eesti, Arial, sans-serif",

	heroDefaultDesktopFontSize: "54px",
	heroDefaultDesktopFontWeight: "900",
	heroDefaultDesktopLineHeight: "100%",

	heroDefaultMobileFontSize: "46px",
	heroDefaultMobileFontWeight: "900",
	heroDefaultMobileLineHeight: "100%",

	titlePrimaryDesktopFontSize: "38px",
	titlePrimaryDesktopFontWeight: "900",
	titlePrimaryDesktopLineHeight: "110%",

	titlePrimaryMobileFontSize: "28px",
	titlePrimaryMobileFontWeight: "900",
	titlePrimaryMobileLineHeight: "110%",

	titleSecondaryFontSize: "22px",
	titleSecondaryFontWeight: "500",
	titleSecondaryLineHeight: "110%",

	titleTertiaryFontSize: "18px",
	titleTertiaryFontWeight: "500",
	titleTertiaryLineHeight: "110%",

	titleLabelDefaultFontSize: "16px",
	titleLabelDefaultFontWeight: "500",
	titleLabelDefaultLineHeight: "110%",

	titleLabelSmallFontSize: "14px",
	titleLabelSmallFontWeight: "500",
	titleLabelSmallLineHeight: "110%",

	bodyDefaultFontSize: "16px",
	bodyDefaultFontWeight: "400",
	bodyDefaultLineHeight: "120%",

	bodySmallFontSize: "14px",
	bodySmallFontWeight: "400",
	bodySmallLineHeight: "120%",

	bodyTooSmallFontSize: "12px",
	bodyTooSmallFontWeight: "400",
	bodyTooSmallLineHeight: "140%",

	bodyTooSmallMobileFontSize: "10px",
	bodyTooSmallMobileFontWeight: "400",
	bodyTooSmallMobileLineHeight: "140%",

	numberXlDesktopFontSize: "82px",
	numberXlDesktopFontWeight: "500",
	numberXlDesktopLineHeight: "100%",

	numberXlMobileFontSize: "40px",
	numberXlMobileFontWeight: "500",
	numberXlMobileLineHeight: "110%",
});

export const zIndices = stylex.defineConsts({
	dropdown: "100",
	sticky: "900",
	modal: "1000",
	tooltip: "1100",
	toast: "1200",
});
